import React, {useState} from "react";
import {
    FileSearchOutlined,
    IssuesCloseOutlined,
    CheckCircleOutlined,
    ExceptionOutlined,
    WarningOutlined
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import moment from "moment-timezone";
import {Tooltip, Popconfirm} from "antd";
import {ActionButtons} from "./ComponentStyles";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {useStore} from "../../../hooks/useStore";
import {ShiftScoreDetailModal, ShiftScoreObjectionModal} from "./";
import CourierReportProblemModal from "./CourierReportProblemModal/CourierReportProblemModal";
import {ObjectionDetailModal} from "../objectionDetail/ObjectionDetailModal";

/**
 * Puantaj aksiyonları (rapor detayı, onayla, itiraz, itiraz detayı, hata bildir) hem puantaj
 * tablosunda hem birleşik vardiya yönetimi tablosunda kullanılıyor. Yetki mantığı ve modal
 * yönetimi tek yerde kalsın diye buraya alındı.
 *
 * @param {Function} onApproved - onay başarılı olduğunda çağrılır (reportId ile)
 * @param {Function} setReloadData - itiraz kaydedildiğinde listeyi tazeler
 * @param {Function} setReloadCount - onay bekleyen sayacını tazeler
 * @returns {{renderActionButtons: Function, actionModals: React.ReactNode}}
 */
export const useShiftScoreActions = ({onApproved, setReloadData, setReloadCount}) => {
    const {t} = useTranslation(["shiftScore"]);
    const stores = useStore();
    const [selectedItem, setSelectedItem] = useState(null);
    const [approveLoading, setApproveLoading] = useState(false);
    const [modalType, setModalType] = useState(null);
    const [open, setOpen] = useState(false);
    const [objectionType, setObjectionType] = useState("objectionModal");
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [objectionDetailId, setObjectionDetailId] = useState(null);

    const isDedicatedIntegration =
        stores?.meStore?.isPoolCustomer &&
        stores?.meStore?.pool?.integrationType === "dedicatedIntegration";

    const closeUpdateModal = ({reloadData = false}) => {
        if (reloadData) {
            setReloadData && setReloadData();
        }
        setSelectedItem();
        setModalType();
    };

    const approveShiftReport = async reportId => {
        setApproveLoading(true);
        const result = await stores.shiftStore.approveShiftReport({reportId});
        if (result) {
            onApproved && onApproved(reportId);
        }
        setSelectedItem();
        setApproveLoading(false);
    };

    const createObjectionPermission = item => {
        if (item?.hasObjection) {
            return false;
        }
        if (stores.meStore.checkRole("isOperationUser") || stores.meStore.checkRole("isAdmin")) {
            return (
                !item.status ||
                ["waitingApprove", "needFillPackage", "approved"].includes(item.status)
            );
        } else if (stores.meStore.checkRole("isCompanyUser")) {
            return !item.status || ["waitingApprove"].includes(item.status);
        } else {
            return false;
        }
    };

    const renderShiftObjectionTitle = row => {
        return row === "updateModal"
            ? t("shiftScore.actions.updateReport", {ns: "shiftScore"})
            : t("shiftScore.actions.objection", {ns: "shiftScore"});
    };

    const getObjectionModalType = row => {
        if (stores.meStore.checkRole("isOperationUser") || stores.meStore.checkRole("isAdmin")) {
            return "updateModal";
        } else {
            const {date, shiftStartTime, shiftEndTime} = row;

            const shiftStart = moment(`${date} ${shiftStartTime}`, "DD-MM-YYYY HH:mm").tz(
                "Europe/Istanbul"
            );
            const shiftEnd = moment(`${date} ${shiftEndTime}`, "DD-MM-YYYY HH:mm").tz(
                "Europe/Istanbul"
            );
            shiftEnd.isBefore(shiftStart) && shiftEnd.add(1, "days");

            let modaltype = "objectionModal";
            return modaltype;
        }
    };

    const renderActionButtons = (id, item) => {
        const permisions = {
            showReportDetail: stores.meStore.checkRole("notVisibleForCompanyUser"),
            createObjection: createObjectionPermission(item),
            showObjection:
                !["waitingApprove", "needFillPackage", "approved"].includes(item.status) &&
                item?.hasObjection,
            approveReport:
                (stores.meStore.checkRole("isCompanyUser") ||
                    stores.meStore.checkRole("isAdmin") ||
                    !stores.meStore.checkRole("isOperationUser")) &&
                (!item.status || ["waitingApprove", "needFillPackage"].includes(item.status)) &&
                !item?.hasObjection
        };

        return item.isActiveShift && !isDedicatedIntegration ? (
            <ActionButtons>
                <Tooltip title={t("shiftScore.reportError", {ns: "shiftScore"})}>
                    <CustomButton
                        onClick={() => {
                            setSelectedItem(item);
                            setModalType("courierReportProblemModal");
                        }}
                        icon={<WarningOutlined />}
                    />
                </Tooltip>
            </ActionButtons>
        ) : (
            <ActionButtons>
                {permisions.showReportDetail && (
                    <Tooltip title={t("shiftScore.actions.showReportDetail", {ns: "shiftScore"})}>
                        <CustomButton
                            data-testid="detailButton"
                            onClick={() => {
                                setSelectedItem(item);
                                setModalType("detailModal");
                            }}
                            icon={<FileSearchOutlined />}
                        ></CustomButton>
                    </Tooltip>
                )}

                {permisions.approveReport && (
                    <Tooltip
                        title={
                            item?.status !== "needFillPackage"
                                ? t("shiftScore.actions.approveReport", {ns: "shiftScore"})
                                : t("shiftScore.actions.tryApproveWhenNeedFillPackage", {
                                      ns: "shiftScore"
                                  })
                        }
                        placement="bottom"
                    >
                        <Popconfirm
                            title={t("modal.confirmTitle", {ns: "common"})}
                            open={open}
                            onConfirm={async () => {
                                setSelectedItem(item);
                                setReloadCount && setReloadCount();
                                await approveShiftReport(item._id);
                            }}
                            onCancel={() => setOpen(false)}
                            okText={t("modal.yesButton", {ns: "common"})}
                            cancelText={t("modal.noButton", {ns: "common"})}
                            disabled={item.status === "needFillPackage"}
                        >
                            <CustomButton
                                loading={approveLoading && selectedItem?._id === id}
                                data-testid="objectionButton"
                                icon={<CheckCircleOutlined />}
                                disabled={item.status === "needFillPackage"}
                            ></CustomButton>
                        </Popconfirm>
                    </Tooltip>
                )}
                {permisions.createObjection && (
                    <Tooltip
                        title={renderShiftObjectionTitle(getObjectionModalType(item))}
                        placement="bottom"
                    >
                        <CustomButton
                            data-testid="objectionButton"
                            onClick={() => {
                                setSelectedItem(item);
                                setModalType("objectionModal");
                                setObjectionType(getObjectionModalType(item));
                            }}
                            icon={<IssuesCloseOutlined />}
                        ></CustomButton>
                    </Tooltip>
                )}

                {permisions.showObjection && (
                    <Tooltip
                        title={t("shiftScore.actions.showObjectionDetail", {ns: "shiftScore"})}
                    >
                        <CustomButton
                            data-testid="objectionButton"
                            onClick={() => {
                                setDetailModalVisible(true);
                                setObjectionDetailId(item.objections[0]._id);
                            }}
                            icon={<ExceptionOutlined />}
                        ></CustomButton>
                    </Tooltip>
                )}
            </ActionButtons>
        );
    };

    const actionModals = (
        <>
            <ObjectionDetailModal
                isVisible={detailModalVisible}
                setVisible={setDetailModalVisible}
                objectionId={objectionDetailId}
                setReloadData={setReloadData}
                reloadData={false}
            ></ObjectionDetailModal>
            <CourierReportProblemModal
                shift={selectedItem}
                visibility={modalType === "courierReportProblemModal" && Boolean(selectedItem?._id)}
                onCancel={() => closeUpdateModal({reloadData: false})}
            ></CourierReportProblemModal>
            <ShiftScoreDetailModal
                shiftScore={selectedItem}
                visibility={modalType === "detailModal" && Boolean(selectedItem?._id)}
                onCancel={() => closeUpdateModal({reloadData: false})}
            />
            <ShiftScoreObjectionModal
                shiftScore={selectedItem}
                visibility={modalType === "objectionModal" && Boolean(selectedItem?._id)}
                objectionType={objectionType}
                onCancel={() => closeUpdateModal({reloadData: false})}
                closeModal={() => closeUpdateModal({reloadData: true})}
            />
        </>
    );

    return {renderActionButtons, actionModals, isDedicatedIntegration};
};

export default useShiftScoreActions;
